import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const DamacSunCityPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    agreement: false
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission
    console.log('Form submitted:', formData);
  };

  const amenities = [
    {
      title: "Grand Water Fountain",
      description: "Experience the mesmerizing beauty of our grand water fountain, a stunning centerpiece that creates a serene and elegant atmosphere in the heart of the community.",
      image: "/damac/DAMAC Sun City/Sun_City-Amenities-01-Grand-Water-Fountain-4x3.jpg"
    },
    {
      title: "Library & Co-Working Space",
      description: "A modern space designed for productivity and learning, featuring a comprehensive library and flexible co-working areas perfect for professionals and students alike.",
      image: "/damac/DAMAC Sun City/Sun_City-Amenities-02-Library-Co-Working-Space-4x3.jpg"
    },
    {
      title: "Wild Garden Amphitheatre",
      description: "An outdoor venue surrounded by natural beauty, perfect for community events, performances, and gatherings in a picturesque setting.",
      image: "/damac/DAMAC Sun City/Sun_City-Amenities-03-Wild-Garden-Amphitheatre-4x3.jpg"
    },
    {
      title: "Forest Hiking Trails",
      description: "Explore nature through our extensive network of hiking trails, offering residents the perfect opportunity to stay active while enjoying the natural surroundings.",
      image: "/damac/DAMAC Sun City/Sun_City-Amenities-04-Forest-Hiking-Trails-4x3.jpg"
    }
  ];

  const featuredProjects = [
    {
      name: "Sun City Residences",
      location: "Dubailand, Dubai",
      price: "From AED 850,000",
      type: "1-3 Bedrooms, Apartment",
      image: "/damac/DAMAC Sun City/Sun_City-Gallery-00.jpg"
    },
    {
      name: "Sun City Villas",
      location: "Dubailand, Dubai",
      price: "From AED 1,200,000",
      type: "3-4 Bedrooms, Villa",
      image: "/damac/DAMAC Sun City/Sun_City-Gallery-02.jpg"
    }
  ];

  const faqs = [
    {
      question: "Where is DAMAC Sun City located?",
      answer: "DAMAC Sun City is located in Dubailand, Dubai, offering easy access to major highways and key destinations in the city."
    },
    {
      question: "What types of properties are available at Sun City?",
      answer: "Sun City offers a variety of residential options including apartments and villas to suit different lifestyle needs and preferences."
    },
    {
      question: "What amenities are available at Sun City?",
      answer: "The community features world-class amenities including a grand water fountain, library and co-working space, wild garden amphitheatre, and forest hiking trails."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative w-full h-screen flex flex-col items-center justify-center overflow-hidden">
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover z-0"
          poster="/damac/DAMAC Sun City/Sun_City-Homepage_Hero_16x9-1.jpg"
          controls={false}
          disablePictureInPicture
          disableRemotePlayback
          onLoadStart={() => console.log('Video loading started')}
          onCanPlay={() => console.log('Video can play')}
          onError={(e) => console.log('Video error:', e)}
        >
          <source src="/damac/DAMAC Sun City/DP.com_Suncity_Hero_video__16X9_.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        <div className="absolute inset-0 bg-black bg-opacity-40 z-10" />
        <div className="relative z-20 w-full flex flex-col items-center justify-center text-center px-4">
          <h1 className="text-4xl md:text-6xl font-light mb-4 text-white font-sans tracking-tight" style={{fontFamily: 'Roboto, sans-serif'}}>DAMAC SUN CITY</h1>
        </div>
      </section>

      {/* Project Overview */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-6 font-sans" style={{fontFamily: 'Roboto, sans-serif'}}>ABOUT DAMAC SUN CITY</h2>
            <p className="text-lg text-gray-600 max-w-4xl mx-auto font-sans" style={{fontFamily: 'Roboto, sans-serif'}}>
              Live Bright in Dubailand - A vibrant townhouse community in one of Dubai's fastest-growing regions, offering modern living with nature-inspired amenities.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Master Community</h3>
              <p className="text-gray-600">Comprehensive development</p>
            </div>
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Townhouses</h3>
              <p className="text-gray-600">Modern residential units</p>
            </div>
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Nature-Inspired</h3>
              <p className="text-gray-600">Green living environment</p>
            </div>
          </div>
        </div>
      </section>

      {/* The Destination Section */}
      <section className="max-w-6xl mx-auto py-20 px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-light text-gray-900 mb-6 tracking-tight font-sans" style={{fontFamily: 'Roboto, sans-serif'}}>The Destination</h2>
        </div>
        
        {amenities.map((amenity, index) => (
          <div key={index} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index < amenities.length - 1 ? 'mb-20' : ''}`}>
            {index % 2 === 0 ? (
              // Image Left, Text Right
              <>
                <div className="relative">
                  <img src={amenity.image} alt={amenity.title} className="w-full h-96 object-cover rounded-lg" />
                </div>
                <div className="text-left">
                  <h3 className="text-2xl font-light text-gray-900 mb-4 font-sans tracking-tight" style={{fontFamily: 'Roboto, sans-serif'}}>{amenity.title}</h3>
                  <p className="text-lg text-gray-700 leading-relaxed font-sans" style={{fontFamily: 'Roboto, sans-serif'}}>{amenity.description}</p>
                </div>
              </>
            ) : (
              // Text Left, Image Right
              <>
                <div className="text-left">
                  <h3 className="text-2xl font-light text-gray-900 mb-4 font-sans tracking-tight" style={{fontFamily: 'Roboto, sans-serif'}}>{amenity.title}</h3>
                  <p className="text-lg text-gray-700 leading-relaxed font-sans" style={{fontFamily: 'Roboto, sans-serif'}}>{amenity.description}</p>
                </div>
                <div className="relative">
                  <img src={amenity.image} alt={amenity.title} className="w-full h-96 object-cover rounded-lg" />
                </div>
              </>
            )}
          </div>
        ))}
      </section>

      {/* Featured Projects */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-6 font-sans" style={{fontFamily: 'Roboto, sans-serif'}}>FIND YOUR HOME</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {featuredProjects.map((project, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg overflow-hidden">
                <div className="h-48 overflow-hidden">
                  <img 
                    src={project.image} 
                    alt={project.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2 font-sans" style={{fontFamily: 'Roboto, sans-serif'}}>{project.name}</h3>
                  <p className="text-gray-600 mb-2 font-sans" style={{fontFamily: 'Roboto, sans-serif'}}>{project.location}</p>
                  {project.price && (
                    <p className="text-green-600 font-semibold mb-2 font-sans" style={{fontFamily: 'Roboto, sans-serif'}}>{project.price}</p>
                  )}
                  {project.type && (
                    <p className="text-gray-500 text-sm mb-4 font-sans" style={{fontFamily: 'Roboto, sans-serif'}}>{project.type}</p>
                  )}
                  <Link 
                    to="/contact" 
                    className="inline-block px-6 py-3 bg-black text-white font-medium hover:bg-gray-800 transition-colors duration-200 font-sans" 
                    style={{fontFamily: 'Roboto, sans-serif'}}
                  >
                    EXPLORE
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-6 font-sans" style={{fontFamily: 'Roboto, sans-serif'}}>FREQUENTLY ASKED QUESTIONS</h2>
          </div>

          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 font-sans" style={{fontFamily: 'Roboto, sans-serif'}}>{faq.question}</h3>
                <p className="text-gray-600 font-sans" style={{fontFamily: 'Roboto, sans-serif'}}>{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-6 font-sans" style={{fontFamily: 'Roboto, sans-serif'}}>GET IN TOUCH</h2>
            <p className="text-lg text-gray-600 font-sans" style={{fontFamily: 'Roboto, sans-serif'}}>
              Ready to discover your perfect home at DAMAC Sun City? Contact us today.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 font-sans" style={{fontFamily: 'Roboto, sans-serif'}}>Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent font-sans"
                  style={{fontFamily: 'Roboto, sans-serif'}}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 font-sans" style={{fontFamily: 'Roboto, sans-serif'}}>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent font-sans"
                  style={{fontFamily: 'Roboto, sans-serif'}}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 font-sans" style={{fontFamily: 'Roboto, sans-serif'}}>Phone Number</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent font-sans"
                style={{fontFamily: 'Roboto, sans-serif'}}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 font-sans" style={{fontFamily: 'Roboto, sans-serif'}}>Message</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows="4"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent font-sans"
                style={{fontFamily: 'Roboto, sans-serif'}}
              ></textarea>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                name="agreement"
                checked={formData.agreement}
                onChange={handleChange}
                className="h-4 w-4 text-black focus:ring-black border-gray-300 rounded"
              />
              <label className="ml-2 text-sm text-gray-700 font-sans" style={{fontFamily: 'Roboto, sans-serif'}}>
                I agree to receive updates on WhatsApp
              </label>
            </div>
            <div className="text-center">
              <button
                type="submit"
                className="px-8 py-4 bg-black text-white font-medium hover:bg-gray-800 transition-colors duration-200 font-sans"
                style={{fontFamily: 'Roboto, sans-serif'}}
              >
                SUBMIT
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default DamacSunCityPage; 